import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { 
  Plus, Users, Briefcase, User, Crown, Settings, 
  MessageSquare, FolderKanban, Loader2, ChevronRight, ChevronDown,
  UserPlus, Check, Trash2, Shield, Eye, Folder, FolderOpen, EyeOff
} from 'lucide-react';
import { toast } from 'sonner';

const FOLDER_COLORS = ['#6B7280', '#4A9CC7', '#E74C3C', '#F39C12', '#27AE60', '#9B59B6', '#1ABC9C', '#E91E63', '#3F51B5', '#607D8B'];

const Workspaces = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { workspaces, folders, unfiledWorkspaces, currentWorkspace, switchWorkspace, createWorkspace, createFolder, refreshWorkspaces, loading } = useWorkspace();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [selectedWorkspace, setSelectedWorkspace] = useState(null);
  const [expandedFolders, setExpandedFolders] = useState({});
  const [newWorkspace, setNewWorkspace] = useState({
    name: '',
    workspace_type: 'team',
    description: '',
    folder_id: ''
  });
  const [newFolder, setNewFolder] = useState({ name: '', color: '#6B7280' });
  const [inviteData, setInviteData] = useState({ email: '', role: 'member' });
  const [creating, setCreating] = useState(false);
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [inviting, setInviting] = useState(false);

  const toggleFolder = (fid) => setExpandedFolders(p => ({ ...p, [fid]: !p[fid] }));

  const handleCreateFolder = async () => {
    if (!newFolder.name.trim()) return;
    setCreatingFolder(true);
    try {
      await createFolder(newFolder);
      toast.success('Folder created!');
      setCreateFolderOpen(false);
      setNewFolder({ name: '', color: '#6B7280' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create folder');
    } finally {
      setCreatingFolder(false);
    }
  };

  const handleCreateWorkspace = async () => {
    if (!newWorkspace.name.trim()) {
      toast.error('Please enter a workspace name');
      return;
    }

    setCreating(true);
    try {
      await createWorkspace({...newWorkspace, folder_id: newWorkspace.folder_id || null});
      toast.success('Workspace created!');
      setCreateDialogOpen(false);
      setNewWorkspace({ name: '', workspace_type: 'team', description: '', folder_id: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create workspace');
    } finally {
      setCreating(false);
    }
  };

  const handleInviteUser = async () => {
    if (!inviteData.email.trim() || !selectedWorkspace) return;
    
    setInviting(true);
    try {
      await api.post(`/workspaces/${selectedWorkspace.workspace_id}/invite`, {
        email: inviteData.email.trim(),
        role: inviteData.role
      });
      toast.success(`Invited ${inviteData.email} to ${selectedWorkspace.name}`);
      setInviteDialogOpen(false);
      setInviteData({ email: '', role: 'member' });
      setSelectedWorkspace(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to invite user');
    } finally {
      setInviting(false);
    }
  };

  const openInviteDialog = (workspace, e) => {
    e.stopPropagation();
    setSelectedWorkspace(workspace);
    setInviteDialogOpen(true);
  };

  const getWorkspaceIcon = (type) => {
    switch (type) {
      case 'team': return <Users className="w-5 h-5" />;
      case 'client': return <Briefcase className="w-5 h-5" />;
      default: return <User className="w-5 h-5" />;
    }
  };

  const getWorkspaceTypeLabel = (type) => {
    switch (type) {
      case 'team': return 'Team';
      case 'client': return 'Client';
      default: return 'Personal';
    }
  };

  const getRoleBadge = (role) => {
    const roleColors = {
      owner: 'bg-yellow-100 text-yellow-800',
      admin: 'bg-blue-100 text-blue-800',
      member: 'bg-gray-100 text-gray-800',
      viewer: 'bg-gray-100 text-gray-600'
    };
    return (
      <Badge className={`${roleColors[role] || roleColors.member} text-xs`}>
        {role === 'owner' && <Crown className="w-3 h-3 mr-1" />}
        {role === 'admin' && <Shield className="w-3 h-3 mr-1" />}
        {role === 'viewer' && <Eye className="w-3 h-3 mr-1" />}
        {role}
      </Badge>
    );
  };

  // Render workspace card
  const renderWsCard = (ws) => (
    <Card key={ws.workspace_id} className={`cursor-pointer hover:shadow-md dark:bg-slate-800 ${currentWorkspace?.workspace_id === ws.workspace_id ? 'ring-2 ring-[#4A9CC7]' : ''}`} onClick={() => switchWorkspace(ws)}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700">{getWorkspaceIcon(ws.workspace_type)}</div>
            <div>
              <CardTitle className="text-lg dark:text-gray-100">{ws.name}</CardTitle>
              <CardDescription className="text-xs">{getWorkspaceTypeLabel(ws.workspace_type)}</CardDescription>
            </div>
          </div>
          {getRoleBadge(ws.user_role)}
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span className="flex items-center gap-1"><Users className="w-4 h-4" />{ws.member_count || 1}</span>
          <div className="flex gap-1">
            <Button size="sm" variant="ghost" className="h-8 px-2" onClick={(e) => { e.stopPropagation(); navigate(`/chat?workspace=${ws.workspace_id}`); }}><MessageSquare className="w-4 h-4" /></Button>
            {(ws.user_role === 'owner' || ws.user_role === 'admin') && <Button size="sm" variant="ghost" className="h-8 px-2" onClick={(e) => { e.stopPropagation(); navigate(`/workspaces/${ws.workspace_id}/settings`); }}><Settings className="w-4 h-4" /></Button>}
          </div>
        </div>
        {currentWorkspace?.workspace_id === ws.workspace_id && <div className="mt-3 pt-3 border-t flex items-center gap-2 text-[#4A9CC7]"><Check className="w-4 h-4" /><span className="text-sm font-medium">Active</span></div>}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-full">
          <Loader2 className="w-8 h-8 text-[#4A9CC7] animate-spin" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="h-full overflow-auto">
        <div className="p-4 md:p-6 max-w-6xl mx-auto pb-20" data-testid="workspaces-page">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Workspaces</h1>
              <p className="text-gray-500 dark:text-gray-400 mt-1">
                Organize and manage your workspaces
              </p>
            </div>
            
            <div className="flex gap-2">
              {/* Create Folder Button */}
              <Dialog open={createFolderOpen} onOpenChange={setCreateFolderOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="dark:border-slate-600" data-testid="create-folder-page-btn">
                    <Folder className="w-4 h-4 mr-2" />
                    New Folder
                  </Button>
                </DialogTrigger>
                <DialogContent className="dark:bg-slate-800 dark:border-slate-700">
                  <DialogHeader>
                    <DialogTitle className="dark:text-gray-100">Create Folder</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label className="dark:text-gray-300">Folder Name</Label>
                      <Input
                        value={newFolder.name}
                        onChange={(e) => setNewFolder({ ...newFolder, name: e.target.value })}
                        placeholder="My Folder"
                        className="dark:bg-slate-900 dark:border-slate-600"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="dark:text-gray-300">Color</Label>
                      <div className="flex gap-2 flex-wrap">
                        {FOLDER_COLORS.map(c => (
                          <button key={c} onClick={() => setNewFolder({ ...newFolder, color: c })} className={`w-8 h-8 rounded-lg ${newFolder.color === c ? 'ring-2 ring-offset-2 ring-gray-400' : ''}`} style={{ backgroundColor: c }} />
                        ))}
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
                    <Button onClick={handleCreateFolder} disabled={creatingFolder} className="bg-[#4A9CC7] hover:bg-[#3d8ab5]">
                      {creatingFolder && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      Create
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              {/* Create Workspace Button */}
              <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-[#4A9CC7] hover:bg-[#3d8ab5]" data-testid="create-workspace-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    New Workspace
                  </Button>
                </DialogTrigger>
                <DialogContent className="dark:bg-slate-800 dark:border-slate-700">
                  <DialogHeader>
                    <DialogTitle className="dark:text-gray-100">Create Workspace</DialogTitle>
                  </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Workspace Name</Label>
                    <Input
                      value={newWorkspace.name}
                      onChange={(e) => setNewWorkspace({ ...newWorkspace, name: e.target.value })}
                      placeholder="My Team"
                      data-testid="workspace-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Type</Label>
                    <Select
                      value={newWorkspace.workspace_type}
                      onValueChange={(value) => setNewWorkspace({ ...newWorkspace, workspace_type: value })}
                    >
                      <SelectTrigger data-testid="workspace-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="team">
                          <div className="flex items-center gap-2">
                            <Users className="w-4 h-4" />
                            Team - Collaborate with colleagues
                          </div>
                        </SelectItem>
                        <SelectItem value="client">
                          <div className="flex items-center gap-2">
                            <Briefcase className="w-4 h-4" />
                            Client - Project workspace
                          </div>
                        </SelectItem>
                        <SelectItem value="personal">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4" />
                            Personal - Private workspace
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Description (optional)</Label>
                    <Input
                      value={newWorkspace.description}
                      onChange={(e) => setNewWorkspace({ ...newWorkspace, description: e.target.value })}
                      placeholder="What's this workspace for?"
                      data-testid="workspace-desc-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Folder (optional)</Label>
                    <Select
                      value={newWorkspace.folder_id || 'none'}
                      onValueChange={(v) => setNewWorkspace({ ...newWorkspace, folder_id: v === 'none' ? '' : v })}
                    >
                      <SelectTrigger data-testid="workspace-folder-select">
                        <SelectValue placeholder="Select a folder" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Folder (Root)</SelectItem>
                        {folders && folders.map(f => (
                          <SelectItem key={f.folder_id} value={f.folder_id}>{f.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <DialogClose asChild>
                    <Button variant="outline">Cancel</Button>
                  </DialogClose>
                  <Button 
                    onClick={handleCreateWorkspace} 
                    disabled={creating}
                    className="bg-[#4A9CC7] hover:bg-[#3d8ab5]"
                    data-testid="confirm-create-btn"
                  >
                    {creating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Create
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          </div>

          {/* Current Workspace Banner */}
          {currentWorkspace && (
            <Card className="mb-6 bg-gradient-to-r from-[#4A9CC7]/10 to-blue-50 border-[#4A9CC7]/20">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-[#4A9CC7]/20">
                      {getWorkspaceIcon(currentWorkspace.workspace_type)}
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Current Workspace</p>
                      <h3 className="font-semibold text-lg">{currentWorkspace.name}</h3>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {getRoleBadge(currentWorkspace.user_role)}
                    {(currentWorkspace.user_role === 'owner' || currentWorkspace.user_role === 'admin') && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate(`/workspaces/${currentWorkspace.workspace_id}/settings`)}
                        data-testid="current-ws-settings-btn"
                      >
                        <Settings className="w-4 h-4 mr-1" />
                        Settings
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Folders Section */}
          {folders && folders.length > 0 && (
            <div className="mb-8">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                <Folder className="w-5 h-5 text-[#4A9CC7]" />
                Folders
              </h2>
              <div className="space-y-4">
                {folders.map(folder => (
                  <Collapsible key={folder.folder_id} open={expandedFolders[folder.folder_id] !== false} onOpenChange={() => toggleFolder(folder.folder_id)}>
                    <Card className="dark:bg-slate-800 dark:border-slate-700">
                      <CollapsibleTrigger asChild>
                        <CardHeader className="cursor-pointer hover:bg-muted/50 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-6 h-6 flex items-center justify-center">
                              {expandedFolders[folder.folder_id] !== false ? <ChevronDown className="w-5 h-5 text-muted-foreground" /> : <ChevronRight className="w-5 h-5 text-muted-foreground" />}
                            </div>
                            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${folder.color}20`, color: folder.color }}>
                              {expandedFolders[folder.folder_id] !== false ? <FolderOpen className="w-5 h-5" /> : <Folder className="w-5 h-5" />}
                            </div>
                            <div className="flex-1">
                              <CardTitle className="text-lg dark:text-gray-100">{folder.name}</CardTitle>
                              <CardDescription className="text-xs">{(folder.workspaces || []).length} workspace{(folder.workspaces || []).length !== 1 ? 's' : ''}</CardDescription>
                            </div>
                            <Button size="sm" variant="ghost" className="text-[#4A9CC7]" onClick={(e) => { e.stopPropagation(); setNewWorkspace({...newWorkspace, folder_id: folder.folder_id}); setCreateDialogOpen(true); }}>
                              <Plus className="w-4 h-4 mr-1" />
                              Add
                            </Button>
                          </div>
                        </CardHeader>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <CardContent className="pt-0">
                          {(folder.workspaces || []).length === 0 ? (
                            <div className="text-center py-6 text-muted-foreground">No workspaces in this folder</div>
                          ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                              {folder.workspaces.map(ws => renderWsCard(ws))}
                            </div>
                          )}
                        </CardContent>
                      </CollapsibleContent>
                    </Card>
                  </Collapsible>
                ))}
              </div>
            </div>
          )}

          {/* Unfiled Workspaces */}
          {unfiledWorkspaces && unfiledWorkspaces.length > 0 && (
            <div className="mb-8">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                <FolderKanban className="w-5 h-5 text-gray-500" />
                Workspaces <span className="text-sm font-normal text-muted-foreground">(not in folders)</span>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {unfiledWorkspaces.map(ws => renderWsCard(ws))}
              </div>
            </div>
          )}

          {/* Fallback: All workspaces if no folder structure */}
          {(!folders || folders.length === 0) && (!unfiledWorkspaces || unfiledWorkspaces.length === 0) && workspaces && workspaces.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {workspaces.map(ws => renderWsCard(ws))}
            </div>
          )}

          {(!workspaces || workspaces.length === 0) && (!folders || folders.length === 0) && (
            <Card className="p-8 text-center">
              <div className="flex flex-col items-center gap-4">
                <div className="p-4 bg-gray-100 rounded-full">
                  <FolderKanban className="w-8 h-8 text-gray-400" />
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">No workspaces yet</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Create your first workspace to get started
                  </p>
                </div>
                <Button 
                  onClick={() => setCreateDialogOpen(true)}
                  className="bg-[#4A9CC7] hover:bg-[#3d8ab5]"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Workspace
                </Button>
              </div>
            </Card>
          )}

          {/* Invite Dialog */}
          <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <UserPlus className="w-5 h-5 text-[#4A9CC7]" />
                  Invite to {selectedWorkspace?.name}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Email Address</Label>
                  <Input
                    type="email"
                    value={inviteData.email}
                    onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
                    placeholder="colleague@example.com"
                    data-testid="invite-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Role</Label>
                  <Select 
                    value={inviteData.role} 
                    onValueChange={(value) => setInviteData({ ...inviteData, role: value })}
                  >
                    <SelectTrigger data-testid="invite-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">
                        <div className="flex items-center gap-2">
                          <Shield className="w-4 h-4" />
                          Admin - Can manage workspace & members
                        </div>
                      </SelectItem>
                      <SelectItem value="member">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4" />
                          Member - Can create & edit tasks
                        </div>
                      </SelectItem>
                      <SelectItem value="viewer">
                        <div className="flex items-center gap-2">
                          <Eye className="w-4 h-4" />
                          Viewer - Read only access
                        </div>
                      </SelectItem>
                      <SelectItem value="private_contributor">
                        <div className="flex items-center gap-2">
                          <EyeOff className="w-4 h-4" />
                          Private Contributor - Limited visibility
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="outline">Cancel</Button>
                </DialogClose>
                <Button 
                  onClick={handleInviteUser}
                  disabled={inviting || !inviteData.email.trim()}
                  className="bg-[#4A9CC7] hover:bg-[#3d8ab5]"
                  data-testid="confirm-invite-btn"
                >
                  {inviting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}
                  Send Invite
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </AppLayout>
  );
};

export default Workspaces;
